import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { CreditCard, Check, Truck, Users, Briefcase, Loader2, ShieldCheck, ExternalLink, Container, Plus, Minus } from "lucide-react";
import { toast } from "sonner";

const FEATURES = [
  "Full UK (DVSA), Ireland (RSA) & EU compliance tracking",
  "Vehicle inspections, defects, MOT & PMI scheduling",
  "Driver licences, CPC, tachograph & hours",
  "Live GPS tracking & driver mobile app",
  "AI risk scoring, audit PDFs & scheduled email reminders",
];

const LIMIT_META = {
  trucks: { icon: Truck, label: "Trucks", sub: "HGVs / vehicles" },
  trailers: { icon: Container, label: "Trailers", sub: "trailers" },
  drivers: { icon: Users, label: "Drivers", sub: "driver app accounts" },
  office: { icon: Briefcase, label: "Office / team members", sub: "management logins" },
};

const STATUS_META = {
  trialing: { label: "Free trial", cls: "bg-blue-100 text-blue-700" },
  active: { label: "Active subscription", cls: "bg-emerald-100 text-emerald-700" },
  past_due: { label: "Payment due", cls: "bg-amber-100 text-amber-700" },
  expired: { label: "Trial ended", cls: "bg-red-100 text-red-700" },
  exempt: { label: "Full access", cls: "bg-emerald-100 text-emerald-700" },
};

function UsageBar({ kind, used, limit, unlimited }) {
  const meta = LIMIT_META[kind];
  const Icon = meta.icon;
  const over = !unlimited && used >= limit;
  const pct = unlimited ? 12 : Math.min(100, Math.round((used / limit) * 100));
  return (
    <div data-testid={`usage-${kind}`} className="bg-white border border-slate-200 rounded-md p-4">
      <div className="flex items-center gap-2 mb-2">
        <Icon size={16} className="text-slate-500" />
        <span className="text-sm font-semibold text-slate-800">{meta.label}</span>
        <span className={`ml-auto text-sm font-bold ${over ? "text-red-600" : "text-slate-900"}`} data-testid={`usage-count-${kind}`}>{used}/{unlimited ? "∞" : limit}</span>
      </div>
      <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
        <div className={`h-full ${over ? "bg-red-500" : unlimited ? "bg-emerald-500" : "bg-slate-900"}`} style={{ width: `${pct}%` }} />
      </div>
      <p className="text-[11px] text-slate-400 mt-1.5">{meta.sub}{over ? " · limit reached — extra available at additional cost" : unlimited ? " · unlimited on your account" : ""}</p>
    </div>
  );
}

export default function Billing() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState("yearly");
  const [seats, setSeats] = useState(null);
  const [seatBusy, setSeatBusy] = useState(false);

  const [denied, setDenied] = useState(false);

  const load = useCallback(async () => {
    try { const r = await api.get("/billing"); setData(r.data); }
    catch (e) { if (e.response?.status === 403) setDenied(true); else toast.error("Could not load billing"); }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (data?.seats) setSeats(data.seats); }, [data]);

  // Return from Stripe checkout — confirm via status poll
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sid = params.get("session_id");
    if (params.get("checkout") === "cancelled") {
      toast.info("Checkout cancelled");
      window.history.replaceState({}, "", "/billing");
      return;
    }
    if (!sid) return;
    let tries = 0;
    const tick = async () => {
      tries += 1;
      try {
        const r = await api.get(`/billing/status/${sid}`);
        if (r.data.payment_status === "paid" || r.data.status === "completed") {
          toast.success("Subscription confirmed — thank you!");
          window.history.replaceState({}, "", "/billing");
          load();
          return;
        }
      } catch { /* ignore */ }
      if (tries < 6) setTimeout(tick, 2000);
      else { window.history.replaceState({}, "", "/billing"); load(); }
    };
    tick();
  }, [load]);

  const subscribe = async () => {
    setBusy(true);
    try {
      const r = await api.post("/billing/checkout", { origin_url: window.location.origin, plan_id: selectedPlan });
      if (r.data.checkout_url) window.location.href = r.data.checkout_url;
    } catch (e) { toast.error(e.response?.data?.detail || "Could not start checkout"); setBusy(false); }
  };

  const manage = async () => {
    setBusy(true);
    try {
      const r = await api.post("/billing/portal", { origin_url: window.location.origin });
      if (r.data.url) window.location.href = r.data.url;
    } catch (e) { toast.error(e.response?.data?.detail || "Could not open billing portal"); setBusy(false); }
  };

  const saveSeats = async () => {
    setSeatBusy(true);
    try {
      await api.post("/billing/seats", { trucks: seats.trucks || 0, trailers: seats.trailers || 0, drivers: seats.drivers || 0 });
      toast.success("Extra seats updated");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not update seats"); }
    finally { setSeatBusy(false); }
  };

  if (denied) return (
    <div data-testid="billing-denied" className="max-w-md mx-auto mt-16 text-center">
      <div className="w-14 h-14 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-4"><ShieldCheck className="text-slate-400" size={26} /></div>
      <h1 className="font-heading text-2xl font-black text-slate-900">Owners only</h1>
      <p className="text-slate-500 text-sm mt-2">Billing &amp; subscription is only available to the account owner. Please ask your company owner for any billing changes.</p>
    </div>
  );
  if (!data) return <div data-testid="billing-loading" className="flex items-center gap-2 text-slate-500"><Loader2 className="animate-spin" size={18} /> Loading billing…</div>;

  const sm = STATUS_META[data.status] || STATUS_META.trialing;
  const usage = data.usage || {};
  const limits = data.limits || {};
  const plans = data.plans || [];
  const activePlan = plans.find((p) => p.interval === data.active_interval) || plans[0] || { price_label: "£35", period: "/month" };
  const base = data.base_limits || {};
  const seatUnitAmt = data.active_interval === "year" ? (data.seat_price?.year || 50) : (data.seat_price?.month || 5);
  const seatPer = data.active_interval === "year" ? "/year" : "/month";
  const seatCount = seats ? (seats.trucks || 0) + (seats.trailers || 0) + (seats.drivers || 0) : 0;
  const seatsChanged = !!(seats && data.seats && ["trucks", "trailers", "drivers"].some((k) => (seats[k] || 0) !== (data.seats[k] || 0)));

  return (
    <div data-testid="billing-page" className="max-w-5xl">
      <div className="flex items-center gap-3 mb-1">
        <span className="w-11 h-11 rounded-xl bg-slate-900 text-white flex items-center justify-center"><CreditCard size={22} /></span>
        <div>
          <h1 className="font-heading text-3xl sm:text-4xl font-black tracking-tight text-slate-900">Billing & Subscription</h1>
          <p className="text-xs uppercase tracking-[0.2em] text-slate-400 font-semibold mt-1">Plan · usage · payment</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-6">
        {/* Plan card */}
        <div data-testid="plan-card" className="lg:col-span-2 bg-slate-900 text-white rounded-xl p-7 relative overflow-hidden">
          <div className="flex items-start justify-between gap-4">
            <p className="text-[11px] uppercase tracking-[0.25em] text-slate-400 font-bold">HaulCheck Plan</p>
            <span data-testid="billing-status-badge" className={`text-[11px] font-bold uppercase tracking-wider rounded-full px-3 py-1 ${sm.cls}`}>{sm.label}</span>
          </div>

          {data.exempt ? (
            <div data-testid="exempt-plan-body" className="mt-4">
              <p className="font-heading text-3xl font-black">Full, unlimited access</p>
              <p className="mt-2 text-sm text-slate-300">This account has complete access to HaulCheck — no free-trial timer, no subscription and no plan limits.</p>
            </div>
          ) : data.can_subscribe ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4" data-testid="plan-options">
              {plans.map((p) => {
                const active = selectedPlan === p.id;
                return (
                  <button key={p.id} type="button" data-testid={`plan-option-${p.id}`} onClick={() => setSelectedPlan(p.id)}
                    className={`text-left rounded-lg border p-4 transition-all ${active ? "border-white bg-white/10 ring-2 ring-white/50" : "border-slate-700 hover:border-slate-500"}`}>
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-sm font-semibold text-slate-200">{p.name}</span>
                      {p.savings && <span className="text-[9px] font-bold uppercase tracking-wide rounded-full px-2 py-0.5 bg-emerald-500 text-white shrink-0">Best value</span>}
                    </div>
                    <div className="flex items-end gap-1 mt-1.5">
                      <span data-testid={`plan-price-${p.id}`} className="font-heading text-4xl font-black">{p.price_label}</span>
                      <span className="text-slate-400 font-semibold mb-1">{p.period}</span>
                    </div>
                    {p.savings && <p className="text-[11px] text-emerald-300 font-semibold mt-1">{p.savings}</p>}
                  </button>
                );
              })}
            </div>
          ) : (
            <div className="flex items-end gap-1 mt-3">
              <span data-testid="plan-price" className="font-heading text-5xl font-black">{activePlan.price_label}</span>
              <span className="text-slate-400 font-semibold mb-1.5">{activePlan.period}</span>
              <span className="ml-2 mb-1.5 text-xs text-slate-400">· you're on the {activePlan.name?.toLowerCase()} plan</span>
            </div>
          )}

          {data.status === "trialing" && (
            <p data-testid="trial-remaining" className="mt-3 text-sm text-blue-300 font-semibold">{data.trial_days_remaining} days left in your free trial — no charge until it ends.</p>
          )}
          {data.status === "active" && data.cancel_at_period_end && (
            <p className="mt-3 text-sm text-amber-300 font-semibold">Your subscription is set to cancel at the end of the period.</p>
          )}
          {data.status === "expired" && (
            <p className="mt-3 text-sm text-red-300 font-semibold">Your free trial has ended. Your account is now read-only — subscribe to make changes again.</p>
          )}

          <ul className="mt-5 space-y-2">
            {FEATURES.map((f) => (
              <li key={f} className="flex items-start gap-2 text-sm text-slate-200"><Check size={16} className="text-emerald-400 shrink-0 mt-0.5" /> {f}</li>
            ))}
          </ul>

          <div className="mt-7 flex flex-wrap gap-3">
            {data.exempt ? null : data.can_subscribe ? (
              <Button data-testid="subscribe-button" onClick={subscribe} disabled={busy} className="bg-white text-slate-900 hover:bg-slate-100 rounded-md gap-2 font-bold">
                {busy ? <Loader2 className="animate-spin" size={16} /> : <CreditCard size={16} />}
                {data.status === "trialing" ? "Start subscription (free until trial ends)" : "Subscribe now"}
              </Button>
            ) : (
              <Button data-testid="manage-billing-button" onClick={manage} disabled={busy} className="bg-white text-slate-900 hover:bg-slate-100 rounded-md gap-2 font-bold">
                {busy ? <Loader2 className="animate-spin" size={16} /> : <ExternalLink size={16} />} Manage / cancel billing
              </Button>
            )}
          </div>
          {!data.exempt && <p className="mt-3 text-[11px] text-slate-500 flex items-center gap-1.5"><ShieldCheck size={13} /> Secure payment by Stripe. Cancel anytime — no contract.</p>}
        </div>

        {/* Usage / limits */}
        <div className="space-y-4">
          <div>
            <p className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold mb-2">Plan limits & usage</p>
            <div className="space-y-3">
              {["trucks", "trailers", "drivers", "office"].map((k) => (
                <UsageBar key={k} kind={k} used={usage[k] || 0} limit={limits[k] || 0} unlimited={data.unlimited || (limits[k] || 0) >= 100000} />
              ))}
            </div>
          </div>

          {data.exempt ? (
            <div data-testid="exempt-note" className="bg-emerald-50 border border-emerald-200 text-emerald-800 text-xs rounded-md px-4 py-3">
              Your account has <b>unlimited</b> vehicles, trailers, drivers and office members, with no trial or subscription.
            </div>
          ) : (
            <div data-testid="more-trucks-note" className="bg-slate-50 border border-slate-200 text-slate-600 text-xs rounded-md px-4 py-3">
              Your plan includes <b>{base.trucks} vehicles</b> with unlimited trailers &amp; drivers. Need more than {base.trucks} vehicles? <a href="/contact" className="font-semibold text-slate-900 underline">Contact us</a> and we'll sort you out.
            </div>
          )}

          {!data.exempt && (
            <div className="bg-amber-50 border border-amber-200 text-amber-800 text-xs rounded-md px-4 py-3">
              Base plan includes {base.trucks} vehicles, unlimited trailers &amp; drivers and {base.office} office members. Cancel anytime — no contract.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
